import type { Marker } from '../../domain/annotation';
import { spurFuer, type Block, type VideoOverlay } from '../../domain/videoAnnotation';

/**
 * Der Bearbeitungszustand der zeitlich begrenzten Markierungsebene über einem Video.
 *
 * Eigene, React-freie Datei — aus demselben Grund wie `features/annotate/reducer.ts`: Undo,
 * Auswahl und die Zeitgrenzen sind der Teil, an dem sich still etwas verlieren kann, und sie
 * sollen ohne DOM prüfbar bleiben.
 */

export interface VideoEditorState {
  overlay: VideoOverlay;
  /** Kennung des ausgewählten Blocks, oder `null`. Neue Marken gehen in ihn hinein. */
  auswahl: string | null;
  /**
   * Kennung der ausgewählten **Marke**, oder `null`.
   *
   * ⚠️ **Eigene Auswahl neben der des Blocks.** Ohne sie bliebe der Werkzeugleiste nur, den
   * ganzen Block zu löschen — eine von drei Marken loszuwerden hiesse dann, alle drei zu
   * verlieren. Sie gehört zu ihrem Block und fällt mit ihm weg.
   */
  markeAuswahl: string | null;
  /** Stände **vor** der jeweiligen Änderung, jüngster zuletzt. */
  verlauf: VideoOverlay[];
  zukunft: VideoOverlay[];
  /** Läuft gerade ein Ziehen? Dann gibt es **einen** Verlaufseintrag für den ganzen Zug. */
  imZug: boolean;
}

export type VideoEditorAction =
  | { typ: 'ersetzen'; overlay: VideoOverlay }
  | { typ: 'zug-start' }
  | { typ: 'zug-ende' }
  | { typ: 'block-hinzu'; id: string; fromMs: number; toMs: number }
  | { typ: 'block-rand'; id: string; rand: 'anfang' | 'ende'; ms: number }
  | { typ: 'block-verschieben'; id: string; deltaMs: number }
  | { typ: 'marke-hinzu'; marke: Marker }
  | { typ: 'marke-aendern'; marke: Marker }
  | { typ: 'marke-loeschen'; id: string }
  | { typ: 'marke-waehlen'; id: string | null }
  | { typ: 'block-loeschen'; id: string }
  | { typ: 'block-waehlen'; id: string | null }
  /** Beim Verlassen: Blöcke ohne Marken sind nichts, was jemand behalten wollte. */
  | { typ: 'leere-verwerfen' }
  | { typ: 'undo' }
  | { typ: 'redo' };

/**
 * Die kürzeste Dauer, auf die sich ein Block kürzen lässt.
 *
 * ⚠️ **Unterhalb davon ist er mit dem Finger nicht mehr zu fassen** — in der Leiste wäre er ein
 * Strich, den man weder greifen noch wieder verbreitern könnte. Dieselbe Überlegung wie bei den
 * Anfassern der Marken im Foto-Editor: die Trefferfläche muss grösser sein als das Gezeichnete.
 */
export const MIN_BLOCK_MS = 300;

const grenze = (wert: number, min: number, max: number) => Math.min(Math.max(wert, min), max);

export const startZustand = (overlay: VideoOverlay): VideoEditorState => ({
  overlay,
  auswahl: null,
  markeAuswahl: null,
  verlauf: [],
  zukunft: [],
  imZug: false,
});

const gleich = (a: VideoOverlay, b: VideoOverlay) => JSON.stringify(a) === JSON.stringify(b);

/** Eine inhaltliche Änderung: der bisherige Stand wandert in den Verlauf, die Zukunft fällt weg. */
function aendere(
  state: VideoEditorState,
  neu: VideoOverlay,
  auswahl = state.auswahl,
): VideoEditorState {
  if (state.imZug) return { ...state, overlay: neu, auswahl };
  return { ...state, overlay: neu, auswahl, verlauf: [...state.verlauf, state.overlay], zukunft: [] };
}

export function videoReducer(
  state: VideoEditorState,
  action: VideoEditorAction,
): VideoEditorState {
  switch (action.typ) {
    case 'ersetzen':
      return startZustand(action.overlay);

    case 'zug-start':
      return { ...state, imZug: true, verlauf: [...state.verlauf, state.overlay], zukunft: [] };

    case 'zug-ende': {
      const vorher = state.verlauf[state.verlauf.length - 1];
      const ohneBewegung = vorher !== undefined && gleich(vorher, state.overlay);
      return {
        ...state,
        imZug: false,
        verlauf: ohneBewegung ? state.verlauf.slice(0, -1) : state.verlauf,
      };
    }

    case 'block-hinzu': {
      const neu: Block = {
        id: action.id,
        fromMs: action.fromMs,
        toMs: action.toMs,
        track: spurFuer(state.overlay.blocks, action.fromMs, action.toMs),
        markers: [],
      };
      return aendere(
        state,
        { ...state.overlay, blocks: [...state.overlay.blocks, neu] },
        action.id,
      );
    }

    case 'block-rand':
      return mitBlock(state, action.id, (b) => {
        if (action.rand === 'anfang') {
          return { ...b, fromMs: grenze(action.ms, 0, b.toMs - MIN_BLOCK_MS) };
        }
        return {
          ...b,
          toMs: grenze(action.ms, b.fromMs + MIN_BLOCK_MS, state.overlay.durationMs),
        };
      });

    case 'block-verschieben':
      return mitBlock(state, action.id, (b) => {
        const laenge = b.toMs - b.fromMs;
        /*
         * ⚠️ **Am Rand wird angehalten, nicht gekürzt.** Würde der Block beim Anstossen kürzer,
         * hätte ein Hin- und Herschieben ihn nach und nach aufgezehrt.
         */
        const fromMs = grenze(b.fromMs + action.deltaMs, 0, state.overlay.durationMs - laenge);
        return { ...b, fromMs, toMs: fromMs + laenge };
      });

    case 'marke-hinzu': {
      /*
       * ⚠️ **Ohne gewählten Block hätte die Marke keine Zeit, zu der sie gehört** — sie
       * verschwände stumm. Die Bühne legt in diesem Fall vorher einen Block an; hier bleibt
       * der Zustand unberührt, damit kein leerer Verlaufseintrag entsteht.
       */
      if (state.auswahl === null) return state;
      const marke = action.marke;
      // Frisch Gezeichnetes ist ausgewählt: sonst müsste der Prüfer die eben gesetzte Marke
      // erst wieder antippen, um ihre Farbe zu ändern.
      return {
        ...mitBlock(state, state.auswahl, (b) => ({ ...b, markers: [...b.markers, marke] })),
        markeAuswahl: marke.id,
      };
    }

    case 'marke-aendern': {
      const marke = action.marke;
      /*
       * Ersetzen **an Ort und Stelle** — würde die geänderte Marke ans Ende wandern, spränge
       * sie beim blossen Verschieben vor eine Marke, die sie eben noch verdeckt hat.
       */
      return inAllenBloecken(state, (m) => m.map((x) => (x.id === marke.id ? marke : x)));
    }

    case 'marke-loeschen': {
      const geloescht = inAllenBloecken(state, (m) => m.filter((x) => x.id !== action.id));
      return {
        ...geloescht,
        markeAuswahl: state.markeAuswahl === action.id ? null : state.markeAuswahl,
      };
    }

    case 'marke-waehlen':
      return { ...state, markeAuswahl: action.id };

    case 'block-loeschen':
      return aendere(
        state,
        { ...state.overlay, blocks: state.overlay.blocks.filter((b) => b.id !== action.id) },
        state.auswahl === action.id ? null : state.auswahl,
      );

    case 'block-waehlen':
      /*
       * Kein Verlaufseintrag — eine Auswahl ändert die Ebene nicht. Stünde sie im Verlauf,
       * müsste der Prüfer mehrmals Undo tippen, bevor überhaupt etwas verschwindet.
       */
      // Die Markenauswahl fällt mit: sie zeigte sonst auf eine Marke, die zum vorigen Block
      // gehört und gerade gar nicht zu sehen ist.
      return { ...state, auswahl: action.id, markeAuswahl: null };

    case 'leere-verwerfen': {
      const bleiben = state.overlay.blocks.filter((b) => b.markers.length > 0);
      /*
       * ⚠️ **Nur eingreifen, wenn wirklich etwas wegfällt.** Sonst legte jedes Schliessen einen
       * Verlaufseintrag an, und `videoAnnotateSave` hielte einen unveränderten Stand für
       * geändert und schriebe die Datei neu.
       */
      if (bleiben.length === state.overlay.blocks.length) return state;
      const auswahlBleibt = bleiben.some((b) => b.id === state.auswahl);
      return aendere(
        state,
        { ...state.overlay, blocks: bleiben },
        auswahlBleibt ? state.auswahl : null,
      );
    }

    case 'undo': {
      const vorher = state.verlauf[state.verlauf.length - 1];
      if (vorher === undefined) return state;
      return {
        ...state,
        overlay: vorher,
        auswahl: null,
        verlauf: state.verlauf.slice(0, -1),
        zukunft: [...state.zukunft, state.overlay],
      };
    }

    case 'redo': {
      const wieder = state.zukunft[state.zukunft.length - 1];
      if (wieder === undefined) return state;
      return {
        ...state,
        overlay: wieder,
        auswahl: null,
        verlauf: [...state.verlauf, state.overlay],
        zukunft: state.zukunft.slice(0, -1),
      };
    }
  }
}

/**
 * Eine Marke ändern oder löschen, **ohne zu wissen, in welchem Block sie liegt**.
 *
 * Die Bühne kennt die Marke unter dem Finger, nicht ihren Block. Die Kennungen sind eindeutig,
 * also trifft der Durchgang über alle Blöcke genau eine.
 */
function inAllenBloecken(
  state: VideoEditorState,
  aendern: (markers: Marker[]) => Marker[],
): VideoEditorState {
  const blocks = state.overlay.blocks.map((b) => ({ ...b, markers: aendern(b.markers) }));
  return aendere(state, { ...state.overlay, blocks });
}

/** Einen Block ersetzen — **an Ort und Stelle**, damit die Stapelfolge der Leiste ruhig bleibt. */
function mitBlock(
  state: VideoEditorState,
  id: string,
  aendern: (b: Block) => Block,
): VideoEditorState {
  const blocks = state.overlay.blocks.map((b) => (b.id === id ? aendern(b) : b));
  return aendere(state, { ...state.overlay, blocks });
}

